import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import api from '@/services/api';

interface PaymentPageData {
  status: 'payable' | 'already_paid';
  invoice: {
    id: string;
    invoiceNumber: string;
    issuedAt: string;
    dueAt: string | null;
    subtotalCents: number;
    taxCents: number;
    discountCents: number;
    totalCents: number;
    amountPaidCents: number;
    balanceCents: number;
    currency: string;
    notes: string | null;
    lineItems: Array<{
      id: string;
      description: string;
      quantity: number;
      unitPriceCents: number;
      amountCents: number;
      cptCode: string | null;
    }>;
  };
  clinic: {
    name: string;
    logoUrl: string | null;
    email: string;
    phone: string;
  };
  patient: {
    firstName: string;
    lastName: string;
  };
  stripeClientSecret: string | null;
}

const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function PatientPaymentPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PaymentPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api
      .get(`/patient-portal/invoices/${token}`)
      .then((res) => {
        setData(res.data?.data);
        if (res.data?.data?.status === 'already_paid') {
          setPaymentSuccess(true);
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.error?.message || 'Failed to load invoice';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <XCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-lg font-semibold mb-2">Unable to Load Invoice</h2>
            <p className="text-muted-foreground text-center">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Payment Successful</h2>
            <p className="text-muted-foreground text-center mb-4">
              Thank you for your payment of {data?.invoice ? formatCents(data.invoice.totalCents) : ''}.
            </p>
            <p className="text-sm text-muted-foreground">
              Invoice #{data?.invoice?.invoiceNumber}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              A receipt has been sent to your email.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { invoice, clinic, patient } = data;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Clinic Header */}
        <div className="text-center">
          {clinic.logoUrl && (
            <img src={clinic.logoUrl} alt={clinic.name} className="h-12 mx-auto mb-2" />
          )}
          <h1 className="text-xl font-bold">{clinic.name}</h1>
          <p className="text-muted-foreground">Invoice for {patient.firstName} {patient.lastName}</p>
        </div>

        {/* Invoice Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Invoice #{invoice.invoiceNumber}</CardTitle>
              <Badge className="bg-blue-100 text-blue-800">
                Balance Due: {formatCents(invoice.balanceCents)}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Issued: {new Date(invoice.issuedAt).toLocaleDateString()}
              {invoice.dueAt && ` | Due: ${new Date(invoice.dueAt).toLocaleDateString()}`}
            </p>
          </CardHeader>
          <CardContent>
            {/* Line Items */}
            <div className="space-y-1 mb-4">
              <div className="grid grid-cols-12 text-sm font-medium text-muted-foreground border-b pb-2">
                <div className="col-span-5">Description</div>
                <div className="col-span-2">CPT</div>
                <div className="col-span-1 text-right">Qty</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-2 text-right">Amount</div>
              </div>
              {invoice.lineItems.map((item) => (
                <div key={item.id} className="grid grid-cols-12 text-sm py-1.5 border-b border-gray-100">
                  <div className="col-span-5">{item.description}</div>
                  <div className="col-span-2 text-muted-foreground">{item.cptCode || '-'}</div>
                  <div className="col-span-1 text-right">{item.quantity}</div>
                  <div className="col-span-2 text-right">{formatCents(item.unitPriceCents)}</div>
                  <div className="col-span-2 text-right font-medium">{formatCents(item.amountCents)}</div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t pt-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>{formatCents(invoice.subtotalCents)}</span>
              </div>
              {invoice.taxCents > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Tax</span>
                  <span>{formatCents(invoice.taxCents)}</span>
                </div>
              )}
              {invoice.discountCents > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Discount</span>
                  <span className="text-green-600">-{formatCents(invoice.discountCents)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total Due</span>
                <span>{formatCents(invoice.balanceCents)}</span>
              </div>
            </div>

            {invoice.notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded text-sm">
                <p className="font-medium mb-1">Notes</p>
                <p className="text-muted-foreground">{invoice.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Section */}
        <Card>
          <CardHeader>
            <CardTitle>Payment</CardTitle>
          </CardHeader>
          <CardContent>
            {data.stripeClientSecret ? (
              <div>
                {/* Stripe Elements will be mounted here */}
                <p className="text-sm text-muted-foreground mb-4">
                  Enter your payment details below to complete this payment.
                </p>
                <div id="stripe-payment-element" className="min-h-[200px] border rounded-md p-4 bg-white">
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Stripe Elements will be initialized here with the PaymentElement component.
                    Integration requires @stripe/react-stripe-js and @stripe/stripe-js packages.
                  </p>
                </div>
                <Button
                  className="w-full mt-4"
                  size="lg"
                  disabled={paying}
                  onClick={() => {
                    // In production, this calls stripe.confirmPayment()
                    setPaying(true);
                    // Placeholder — actual Stripe Elements integration
                    setTimeout(() => setPaying(false), 2000);
                  }}
                >
                  {paying ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Pay {formatCents(invoice.balanceCents)}
                </Button>
              </div>
            ) : (
              <div className="text-center py-6">
                <AlertCircle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Payment processing is not available for this invoice.
                  Please contact {clinic.name} directly.
                </p>
                {clinic.phone && <p className="text-sm mt-1">Phone: {clinic.phone}</p>}
                {clinic.email && <p className="text-sm mt-1">Email: {clinic.email}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Powered by Novamed | Secure payment processing by Stripe
        </p>
      </div>
    </div>
  );
}
